/**
 * Subscriptions Mutation Hooks
 *
 * TanStack Query mutation hooks for subscription operations
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CancelSubscriptionRequest,
  GrantFreeDaysRequest,
  PauseSubscriptionRequest,
} from '@/types/subscription';
import { subscriptionsClient } from '../client/subscriptions.client';
import { queryKeys } from '../query-keys';

/**
 * Hook to create subscription via Apple In-App Purchase
 */
export function useSubscribeApple() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (receipt: string) => subscriptionsClient.createApple(receipt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.all });
    },
  });
}

/**
 * Hook to create subscription via Google Play
 */
export function useSubscribeGooglePlay() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ purchaseToken, productId }: { purchaseToken: string; productId: string }) =>
      subscriptionsClient.createGooglePlay(purchaseToken, productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.all });
    },
  });
}

/**
 * Hook to cancel subscription (Whop in-app cancellation)
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CancelSubscriptionRequest) => subscriptionsClient.cancelSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.all });
    },
  });
}

/**
 * Hook to pause subscription. Whop pause is indefinite — BE owns
 * `paused_until` + a 15-min cron auto-resume.
 */
export function usePauseSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PauseSubscriptionRequest) => subscriptionsClient.pauseSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.all });
    },
  });
}

/**
 * Hook to resume a paused subscription. Idempotent — 200 no-op if already active.
 */
export function useResumeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => subscriptionsClient.resumeSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.all });
    },
  });
}

/**
 * Hook to grant free days as a retention save (Whop add_free_days).
 * BE enforces a 3-month cooldown — surfaces 409 COOLDOWN_ACTIVE with
 * `next_eligible_at` for FE copy.
 */
export function useGrantFreeDays() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GrantFreeDaysRequest = {}) => subscriptionsClient.grantFreeDays(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription.all });
    },
  });
}
